import { FC, memo, useContext, useEffect, useState } from "react";
import { InView } from "react-intersection-observer";
import { PdpCarouselTargetType, ProductGender } from "src/types/graphql.d";
import usePdpCarousels from "src/hooks/usePdpCarousels";
import { LoadingSpinner } from "src/atoms/LoadingSpinner";
import { ProductCarousel } from "src/molecules/ProductCarousel";
import {
  CardsLoader,
  ProductViewCarouselsContent,
  StyledCarousels,
} from "./ProductView.styled";
import getCollectionsRoute from "src/utils/getCollectionsRoute";
import { AppCtx } from "src/contexts/app.context";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { GATED_PRODUCT_PAGE } from "src/constants/gatedItem";

interface ProductViewCarouselsProps {
  slug: string;
  shopType: ProductGender;
}

const Carousel = memo(ProductCarousel);

const ProductViewCarousels: FC<ProductViewCarouselsProps> = ({
  shopType,
  slug,
}) => {
  const { enableWishListCarousel } = useFeatureFlags();
  const { locale } = useContext(AppCtx);
  const { data, loading } = usePdpCarousels({
    slug,
    shopType,
  });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const getViewAllLink = (
    targetType: PdpCarouselTargetType,
    targetLink: string
  ) => {
    return targetType === PdpCarouselTargetType.CollectionPlp
      ? getCollectionsRoute({
          shopType,
          collectionsSlug: targetLink,
          locale,
        })
      : "";
  };

  const pdpCarousels = enableWishListCarousel
    ? data?.pdpCarousels?.carousels
    : data?.pdpCarousels?.carousels?.filter(
        (carousel) => carousel.id !== "pdp_favorite_items_carousel"
      );

  return (
    <InView rootMargin={"150px 0px 0px 0px"} triggerOnce={true}>
      {({ inView, ref }) => (
        <ProductViewCarouselsContent ref={ref}>
          {loading && isClient ? (
            <CardsLoader>
              <LoadingSpinner />
            </CardsLoader>
          ) : (
            pdpCarousels?.map(
              (carousel) =>
                (inView || !isClient) && (
                  <StyledCarousels key={carousel.id} data-testid={carousel.id}>
                    <Carousel
                      genderId={shopType}
                      title={carousel.title}
                      products={carousel.products}
                      titleForTracking={carousel.title}
                      shouldReset={true}
                      slidesDesktop={6}
                      slidesMobile={2.5}
                      slidesTablet={6}
                      zoomImageOnHover={true}
                      headerLinkHref={
                        carousel.target_link && carousel.target_type
                          ? getViewAllLink(
                              carousel.target_type,
                              carousel.target_link
                            )
                          : ""
                      }
                      responseSourceType={carousel.response_source_type}
                      trackingData={{
                        shopType,
                        pageName: GATED_PRODUCT_PAGE,
                        carouselType: carousel.id,
                      }}
                      isPDPCarousel
                    />
                  </StyledCarousels>
                )
            )
          )}
        </ProductViewCarouselsContent>
      )}
    </InView>
  );
};

export default ProductViewCarousels;
